import type { StateCreator } from 'zustand';
import type { AppStore } from '../types';
import type { BoxCount } from '@/components/ElkoninBoxes';
import type { TapRegionCount } from '@/components/SoundTapPhase';
import type { GuideLineType } from '@/components/DrawingCanvas';

/**
 * Learning mode type for WB-01
 * - 'teacher-led': Teacher provides verbal instruction (current model)
 * - 'self-learn': Student selects words from word bank and hears them via TTS
 */
export type LearningMode = 'teacher-led' | 'self-learn';

/**
 * Configuration preset type
 */
export interface ConfigPreset {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  wb01Config: WB01Config;
  wb02Config: WB02Config;
}

/**
 * WB-01 Configuration (Phoneme Segmentation)
 */
export interface WB01Config {
  /** Number of Elkonin boxes (2-6) */
  boxCount: BoxCount;
  /** Letters to hide from the alphabet arc */
  hiddenLetters: string[];
  /** Enabled consonant digraphs */
  enabledDigraphs: string[];
  /** Enabled vowel teams */
  enabledVowelTeams: string[];
  /** Target word for auto-checking (optional) */
  targetWord: string;
  /** Expected segmentation for target word */
  targetSegmentation: string[];
  /** Learning mode: teacher-led or self-learn */
  learningMode: LearningMode;
  /** Active word ID for self-learn mode (from word bank) */
  activeWordId: string | null;
}

/**
 * WB-02 Configuration (Write to Reveal)
 */
export interface WB02Config {
  /** Number of tap regions for Sound Tap phase (1-6) */
  tapRegionCount: TapRegionCount;
  /** Whether guide lines are shown on drawing canvas */
  guideLinesEnabled: boolean;
  /** Which guide lines to show */
  guideLineTypes: GuideLineType[];
  /** Target word for auto-checking (optional) */
  targetWord: string;
}

/**
 * Whiteboard configuration state
 */
export interface WhiteboardConfigState {
  wb01Config: WB01Config;
  wb02Config: WB02Config;
  presets: ConfigPreset[];
  activePresetId: string | null;
}

/**
 * Whiteboard configuration actions
 */
export interface WhiteboardConfigActions {
  // WB-01 actions
  setWB01BoxCount: (count: BoxCount) => void;
  setWB01HiddenLetters: (letters: string[]) => void;
  toggleWB01Letter: (letter: string) => void;
  setWB01EnabledDigraphs: (digraphs: string[]) => void;
  toggleWB01Digraph: (digraph: string) => void;
  setWB01EnabledVowelTeams: (vowelTeams: string[]) => void;
  toggleWB01VowelTeam: (vowelTeam: string) => void;
  setWB01TargetWord: (word: string, segmentation: string[]) => void;
  clearWB01TargetWord: () => void;
  setWB01LearningMode: (mode: LearningMode) => void;
  setWB01ActiveWord: (wordId: string | null) => void;

  // WB-02 actions
  setWB02TapRegionCount: (count: TapRegionCount) => void;
  setWB02GuideLinesEnabled: (enabled: boolean) => void;
  setWB02GuideLineTypes: (types: GuideLineType[]) => void;
  toggleWB02GuideLine: (type: GuideLineType) => void;
  setWB02TargetWord: (word: string) => void;
  clearWB02TargetWord: () => void;

  // Preset actions
  savePreset: (name: string) => string;
  loadPreset: (presetId: string) => boolean;
  deletePreset: (presetId: string) => void;
  updatePresetName: (presetId: string, name: string) => void;

  // Reset actions
  resetWB01Config: () => void;
  resetWB02Config: () => void;
  resetAllConfig: () => void;
}

export type WhiteboardConfigSlice = WhiteboardConfigState & WhiteboardConfigActions;

/**
 * Default WB-01 configuration
 */
export const DEFAULT_WB01_CONFIG: WB01Config = {
  boxCount: 3,
  hiddenLetters: [],
  enabledDigraphs: [],
  enabledVowelTeams: [],
  targetWord: '',
  targetSegmentation: [],
  learningMode: 'teacher-led',
  activeWordId: null,
};

/**
 * Default WB-02 configuration
 */
export const DEFAULT_WB02_CONFIG: WB02Config = {
  tapRegionCount: 3,
  guideLinesEnabled: false,
  guideLineTypes: ['baseline', 'midline'],
  targetWord: '',
};

/**
 * Generate unique ID for presets
 */
function generatePresetId(): string {
  return `preset-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Validate box count is within bounds
 */
function validateBoxCount(count: number): BoxCount {
  if (count < 2) return 2;
  if (count > 6) return 6;
  return count as BoxCount;
}

/**
 * Validate tap region count is within bounds
 */
function validateTapRegionCount(count: number): TapRegionCount {
  if (count < 1) return 1;
  if (count > 6) return 6;
  return count as TapRegionCount;
}

/**
 * Create whiteboard configuration slice
 */
export const createWhiteboardConfigSlice: StateCreator<
  AppStore,
  [],
  [],
  WhiteboardConfigSlice
> = (set, get) => ({
  // Initial state
  wb01Config: { ...DEFAULT_WB01_CONFIG },
  wb02Config: { ...DEFAULT_WB02_CONFIG },
  presets: [],
  activePresetId: null,

  // WB-01 actions
  setWB01BoxCount: (count) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        boxCount: validateBoxCount(count),
      },
    })),

  setWB01HiddenLetters: (letters) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        hiddenLetters: letters.map((l) => l.toLowerCase()),
      },
    })),

  toggleWB01Letter: (letter) =>
    set((state) => {
      const lowerLetter = letter.toLowerCase();
      const hiddenLetters = state.wb01Config.hiddenLetters.includes(lowerLetter)
        ? state.wb01Config.hiddenLetters.filter((l) => l !== lowerLetter)
        : [...state.wb01Config.hiddenLetters, lowerLetter];
      return {
        wb01Config: {
          ...state.wb01Config,
          hiddenLetters,
        },
      };
    }),

  setWB01EnabledDigraphs: (digraphs) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        enabledDigraphs: digraphs.map((d) => d.toLowerCase()),
      },
    })),

  toggleWB01Digraph: (digraph) =>
    set((state) => {
      const lowerDigraph = digraph.toLowerCase();
      const enabledDigraphs = state.wb01Config.enabledDigraphs.includes(lowerDigraph)
        ? state.wb01Config.enabledDigraphs.filter((d) => d !== lowerDigraph)
        : [...state.wb01Config.enabledDigraphs, lowerDigraph];
      return {
        wb01Config: {
          ...state.wb01Config,
          enabledDigraphs,
        },
      };
    }),

  setWB01EnabledVowelTeams: (vowelTeams) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        enabledVowelTeams: vowelTeams.map((v) => v.toLowerCase()),
      },
    })),

  toggleWB01VowelTeam: (vowelTeam) =>
    set((state) => {
      const lowerVowelTeam = vowelTeam.toLowerCase();
      const enabledVowelTeams = state.wb01Config.enabledVowelTeams.includes(lowerVowelTeam)
        ? state.wb01Config.enabledVowelTeams.filter((v) => v !== lowerVowelTeam)
        : [...state.wb01Config.enabledVowelTeams, lowerVowelTeam];
      return {
        wb01Config: {
          ...state.wb01Config,
          enabledVowelTeams,
        },
      };
    }),

  setWB01TargetWord: (word, segmentation) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        targetWord: word.toLowerCase(),
        targetSegmentation: segmentation.map((s) => s.toLowerCase()),
        boxCount: validateBoxCount(segmentation.length),
      },
    })),

  clearWB01TargetWord: () =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        targetWord: '',
        targetSegmentation: [],
      },
    })),

  setWB01LearningMode: (mode) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        learningMode: mode,
        // Reset active word when switching modes
        activeWordId: mode === 'teacher-led' ? null : state.wb01Config.activeWordId,
      },
    })),

  setWB01ActiveWord: (wordId) =>
    set((state) => ({
      wb01Config: {
        ...state.wb01Config,
        activeWordId: wordId,
      },
    })),

  // WB-02 actions
  setWB02TapRegionCount: (count) =>
    set((state) => ({
      wb02Config: {
        ...state.wb02Config,
        tapRegionCount: validateTapRegionCount(count),
      },
    })),

  setWB02GuideLinesEnabled: (enabled) =>
    set((state) => ({
      wb02Config: {
        ...state.wb02Config,
        guideLinesEnabled: enabled,
      },
    })),

  setWB02GuideLineTypes: (types) =>
    set((state) => ({
      wb02Config: {
        ...state.wb02Config,
        guideLineTypes: types,
      },
    })),

  toggleWB02GuideLine: (type) =>
    set((state) => {
      const guideLineTypes = state.wb02Config.guideLineTypes.includes(type)
        ? state.wb02Config.guideLineTypes.filter((t) => t !== type)
        : [...state.wb02Config.guideLineTypes, type];
      return {
        wb02Config: {
          ...state.wb02Config,
          guideLineTypes,
        },
      };
    }),

  setWB02TargetWord: (word) =>
    set((state) => ({
      wb02Config: {
        ...state.wb02Config,
        targetWord: word.toLowerCase(),
      },
    })),

  clearWB02TargetWord: () =>
    set((state) => ({
      wb02Config: {
        ...state.wb02Config,
        targetWord: '',
      },
    })),

  // Preset actions
  savePreset: (name) => {
    const state = get();
    const id = generatePresetId();
    const now = new Date().toISOString();
    const preset: ConfigPreset = {
      id,
      name,
      createdAt: now,
      updatedAt: now,
      wb01Config: { ...state.wb01Config },
      wb02Config: { ...state.wb02Config },
    };
    set((state) => ({
      presets: [...state.presets, preset],
      activePresetId: id,
    }));
    return id;
  },

  loadPreset: (presetId) => {
    const state = get();
    const preset = state.presets.find((p) => p.id === presetId);
    if (!preset) return false;
    set({
      wb01Config: { ...preset.wb01Config },
      wb02Config: { ...preset.wb02Config },
      activePresetId: presetId,
    });
    return true;
  },

  deletePreset: (presetId) =>
    set((state) => ({
      presets: state.presets.filter((p) => p.id !== presetId),
      activePresetId: state.activePresetId === presetId ? null : state.activePresetId,
    })),

  updatePresetName: (presetId, name) =>
    set((state) => ({
      presets: state.presets.map((p) =>
        p.id === presetId
          ? { ...p, name, updatedAt: new Date().toISOString() }
          : p
      ),
    })),

  // Reset actions
  resetWB01Config: () =>
    set({
      wb01Config: { ...DEFAULT_WB01_CONFIG },
    }),

  resetWB02Config: () =>
    set({
      wb02Config: { ...DEFAULT_WB02_CONFIG },
    }),

  resetAllConfig: () =>
    set({
      wb01Config: { ...DEFAULT_WB01_CONFIG },
      wb02Config: { ...DEFAULT_WB02_CONFIG },
      activePresetId: null,
    }),
});
